import { useCallback, useEffect, useRef, useState } from 'react';
import { App, Button, Modal, Pagination } from 'antd';
import { CopyOutlined, EyeOutlined, GiftOutlined, PlusOutlined } from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../Home/Home.css';
import './Vouchers.css';
import { AppApc } from '../../api/appApc';
import {
    getVoucherAmount,
    getVoucherCode,
    getVoucherDate,
    getVoucherId,
    getVoucherLogo,
    getVoucherName,
    getVoucherRegion,
    getVoucherUsageStatus,
    type MyVoucher,
    type VoucherUsageStatus,
} from './mapVoucher';

type LocationState = {
    voucherId?: string;
};

const PAGE_SIZE = 6;
type VoucherTab = 'active' | 'used';

function isHttpUrl(value: string) {
    return /^https?:\/\//i.test(value.trim());
}

function VouchersPage() {
    const { t } = useTranslation('vouchers');
    const { message } = App.useApp();
    const location = useLocation();
    const voucherId = (location.state as LocationState | null)?.voucherId;
    const [selected, setSelected] = useState<MyVoucher | null>(null);
    const [revealed, setRevealed] = useState(false);
    const [myVoucher, setMyVoucher] = useState<MyVoucher[]>([]);
    const [tab, setTab] = useState<VoucherTab>('active');
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [detailLoading, setDetailLoading] = useState(false);
    const [pendingIds, setPendingIds] = useState<string[]>([]);
    const detailRequestRef = useRef(0);
    const pendingRef = useRef(new Set<string>());
    const openedFromRouteRef = useRef<string | null>(null);

    const listMyVoucher = async () => {
        try {
            const response = await AppApc.GetListMyVoucher();
            if (response.status === 200) {
                setMyVoucher(Array.isArray(response?.data?.data) ? response.data.data : []);
            }
        } catch (error) {
            console.error('Error fetching list my voucher', error);
            setMyVoucher([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        listMyVoucher();
    }, []);

    const openVoucher = useCallback(async (voucher: MyVoucher) => {
        setSelected(voucher);
        setRevealed(Boolean(voucher.revealed));

        const id = getVoucherId(voucher);
        if (!id) return;

        const requestId = ++detailRequestRef.current;
        setDetailLoading(true);

        try {
            const response = await AppApc.GetVoucherOrder(id);
            const detail = response.data?.data ?? response.data;

            if (response.status === 200 && detail && typeof detail === 'object') {
                setSelected((current) =>
                    current && getVoucherId(current) === id
                        ? { ...current, ...(detail as MyVoucher) }
                        : current
                );
            }
        } catch (error) {
            console.error('Error fetching voucher details', error);
            message.error(t('messages.loadDetailsFailed'));
        } finally {
            if (detailRequestRef.current === requestId) {
                setDetailLoading(false);
            }
        }
    }, [message, t]);

    useEffect(() => {
        if (!voucherId || !myVoucher.length || openedFromRouteRef.current === voucherId) return;
        const match = myVoucher.find((item) => getVoucherId(item) === voucherId);
        if (!match) return;
        openedFromRouteRef.current = voucherId;
        setTab(getVoucherUsageStatus(match) === 'USED' ? 'used' : 'active');
        void openVoucher(match);
    }, [voucherId, myVoucher, openVoucher]);

    const setUsagePending = (id: string, pending: boolean) => {
        if (pending) pendingRef.current.add(id);
        else pendingRef.current.delete(id);
        setPendingIds((current) =>
            pending
                ? current.includes(id)
                    ? current
                    : [...current, id]
                : current.filter((item) => item !== id)
        );
    };

    const updateVoucherUsage = async (voucher: MyVoucher, nextStatus: VoucherUsageStatus) => {
        const id = getVoucherId(voucher);
        if (!id || pendingRef.current.has(id)) return;

        setUsagePending(id, true);
        try {
            const response = await AppApc.UpdateVoucherUsage(id, nextStatus);
            if (response.status < 200 || response.status >= 300) {
                message.error(t('messages.statusUpdateFailed'));
                return;
            }

            setMyVoucher((current) =>
                current.map((item) =>
                    getVoucherId(item) === id ? { ...item, status: nextStatus } : item
                )
            );
            message.success(
                t(nextStatus === 'USED' ? 'messages.markedUsed' : 'messages.markedReady')
            );
        } catch (error) {
            console.error('Error updating voucher usage', error);
            message.error(t('messages.statusUpdateFailed'));
        } finally {
            setUsagePending(id, false);
        }
    };

    const closeVoucher = () => {
        detailRequestRef.current += 1;
        setDetailLoading(false);
        setSelected(null);
    };

    const copyVoucherValue = async (value: string | undefined, labelKey: 'code' | 'pin') => {
        const label = t(`labels.${labelKey}`);
        if (!value) {
            message.error(t('messages.unavailable', { label }));
            return;
        }
        try {
            await navigator.clipboard.writeText(value);
            message.success(t('messages.copied', { label }));
        } catch {
            message.error(t('messages.copyFailed', { label }));
        }
    };

    const selectedCode = selected ? getVoucherCode(selected) : '';
    const codeIsHttpUrl = isHttpUrl(selectedCode);
    const activeVouchers = myVoucher.filter((voucher) => getVoucherUsageStatus(voucher) === 'READY');
    const usedVouchers = myVoucher.filter((voucher) => getVoucherUsageStatus(voucher) === 'USED');
    const visibleVouchers = tab === 'used' ? usedVouchers : activeVouchers;
    const pageCount = Math.max(1, Math.ceil(visibleVouchers.length / PAGE_SIZE));
    const currentPage = Math.min(page, pageCount);
    const pagedVouchers = visibleVouchers.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
    );

    useEffect(() => {
        if (page > pageCount) setPage(pageCount);
    }, [page, pageCount]);

    return (
        <div className="vouchers-page">
            <div className="page-heading">
                <div>
                    <h1>{t('heading.title')}</h1>
                    <p>{t('heading.subtitle')}</p>
                </div>
                <Link className="vouchers-page__browse" to="/explore">
                    <PlusOutlined />
                    {t('actions.browse')}
                </Link>
            </div>

            <div className="section-head">
                <div className="vouchers-page__tablist" role="tablist" aria-label={t('tabs.a11y')}>
                    {(['active', 'used'] as const).map((item) => {
                        const count = item === 'used' ? usedVouchers.length : activeVouchers.length;
                        return (
                            <button
                                key={item}
                                type="button"
                                role="tab"
                                id={`voucher-tab-${item}`}
                                aria-selected={tab === item}
                                aria-controls="voucher-tabpanel"
                                className={`vouchers-page__tab${tab === item ? ' is-active' : ''}`}
                                onClick={() => {
                                    setTab(item);
                                    setPage(1);
                                }}
                            >
                                {t(`tabs.${item}`)}
                                {loading ? null : (
                                    <span className="vouchers-page__tab-count">{count}</span>
                                )}
                            </button>
                        );
                    })}
                </div>
                {loading ? (
                    <span className="voucher-skel voucher-skel--count" />
                ) : (
                    <small>{t('ready.count', { count: visibleVouchers.length })}</small>
                )}
            </div>

            {loading ? (
                <div
                    className="catalog vouchers-page__catalog"
                    id="voucher-tabpanel"
                    role="tabpanel"
                    aria-labelledby={`voucher-tab-${tab}`}
                    aria-busy="true"
                    aria-label={t('a11y.loadingVouchers')}
                >
                    {Array.from({ length: PAGE_SIZE }).map((_, index) => (
                        <div
                            className="product-card voucher-card voucher-card--skeleton"
                            key={`skel-${index}`}
                            aria-hidden
                        >
                            <div className="voucher-card__media">
                                <span className="voucher-skel voucher-skel--logo" />
                            </div>
                            <div className="voucher-card__body">
                                <span className="voucher-skel voucher-skel--title" />
                                <span className="voucher-skel voucher-skel--meta" />
                            </div>
                            <div className="voucher-card__footer">
                                <span className="voucher-skel voucher-skel--amount" />
                                <span className="voucher-card__actions" aria-hidden>
                                    <span className="voucher-skel voucher-skel--cta" />
                                    <span className="voucher-skel voucher-skel--cta" />
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            ) : visibleVouchers.length ? (
                <div
                    className="catalog vouchers-page__catalog"
                    id="voucher-tabpanel"
                    role="tabpanel"
                    aria-labelledby={`voucher-tab-${tab}`}
                >
                    {pagedVouchers.map((voucher) => {
                        const displayName = getVoucherName(voucher);
                        const logoUrl = getVoucherLogo(voucher);
                        const region = getVoucherRegion(voucher);
                        const amountLabel = getVoucherAmount(voucher);
                        const purchased = getVoucherDate(voucher);
                        const meta = [region, purchased].filter(Boolean).join(' · ');
                        const voucherKey = getVoucherId(voucher);
                        const isUsed = getVoucherUsageStatus(voucher) === 'USED';
                        const nextStatus: VoucherUsageStatus = isUsed ? 'READY' : 'USED';
                        const isUpdating = pendingIds.includes(voucherKey);

                        return (
                            <article
                                className="product-card voucher-card"
                                key={voucherKey || displayName}
                            >
                                <button
                                    type="button"
                                    className="voucher-card__open"
                                    aria-label={t('a11y.openVoucher', { name: displayName })}
                                    onClick={() => openVoucher(voucher)}
                                >
                                    <div className="voucher-card__media">
                                        <div className="voucher-card__glow" aria-hidden />
                                        <span
                                            className={`voucher-card__badge${
                                                isUsed ? ' voucher-card__badge--used' : ''
                                            }`}
                                        >
                                            {isUsed ? t('used.badge') : t('ready.badge')}
                                        </span>
                                        <div
                                            className={`voucher-card__logo${
                                                logoUrl ? ' voucher-card__logo--image' : ''
                                            }`}
                                        >
                                            {logoUrl ? (
                                                <img src={logoUrl} alt="" loading="lazy" />
                                            ) : (
                                                <span>{displayName.charAt(0)}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="voucher-card__body">
                                        <strong className="voucher-card__name">{displayName}</strong>
                                        <span className="voucher-card__meta">{meta}</span>
                                    </div>
                                </button>
                                <div className="voucher-card__footer">
                                    <span className="voucher-card__amount">
                                        <em>{amountLabel}</em>
                                    </span>
                                    <div className="voucher-card__actions">
                                        <button
                                            type="button"
                                            className="voucher-card__usage"
                                            aria-label={t(
                                                isUsed ? 'a11y.markReady' : 'a11y.markUsed',
                                                { name: displayName }
                                            )}
                                            disabled={!voucherKey || isUpdating}
                                            onClick={() =>
                                                void updateVoucherUsage(voucher, nextStatus)
                                            }
                                        >
                                            {isUsed ? t('actions.markReady') : t('actions.markUsed')}
                                        </button>
                                        <button
                                            type="button"
                                            className="voucher-card__cta"
                                            onClick={() => openVoucher(voucher)}
                                        >
                                            {t('actions.view')}
                                        </button>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            ) : (
                <div
                    className="vouchers-page__empty"
                    id="voucher-tabpanel"
                    role="tabpanel"
                    aria-labelledby={`voucher-tab-${tab}`}
                >
                    <GiftOutlined className="vouchers-page__empty-icon" />
                    {myVoucher.length === 0 ? (
                        <>
                            <h3>{t('empty.title')}</h3>
                            <p>{t('empty.description')}</p>
                            <Link className="vouchers-page__browse" to="/explore">
                                <PlusOutlined />
                                {t('actions.browseGiftCards')}
                            </Link>
                        </>
                    ) : tab === 'used' ? (
                        <>
                            <h3>{t('used.emptyTitle')}</h3>
                            <p>{t('used.emptyDescription')}</p>
                        </>
                    ) : (
                        <>
                            <h3>{t('activeEmpty.title')}</h3>
                            <p>{t('activeEmpty.description')}</p>
                        </>
                    )}
                </div>
            )}

            {!loading && visibleVouchers.length > PAGE_SIZE ? (
                <div className="vouchers-page__pagination">
                    <Pagination
                        current={currentPage}
                        pageSize={PAGE_SIZE}
                        total={visibleVouchers.length}
                        showSizeChanger={false}
                        onChange={(nextPage) => setPage(nextPage)}
                    />
                </div>
            ) : null}

            <Modal
                open={Boolean(selected)}
                onCancel={closeVoucher}
                footer={null}
                centered
                className="topup-modal"
                title={selected ? t('detail.title', { name: getVoucherName(selected) }) : ''}
                destroyOnClose
            >
                {selected && detailLoading ? (
                    <div
                        className="voucher-detail voucher-detail--loading"
                        aria-busy="true"
                        aria-label={t('a11y.loadingDetails')}
                    >
                        <div className="voucher-detail__hero">
                            <span className="voucher-skel voucher-detail__skel-logo" />
                            <div className="voucher-detail__skel-copy">
                                <span className="voucher-skel voucher-detail__skel-title" />
                                <span className="voucher-skel voucher-detail__skel-meta" />
                                <span className="voucher-skel voucher-detail__skel-amount" />
                            </div>
                        </div>
                        <div className="voucher-detail__skel-row">
                            <span className="voucher-skel" />
                            <span className="voucher-skel" />
                        </div>
                        <div className="voucher-detail__skel-row">
                            <span className="voucher-skel" />
                            <span className="voucher-skel" />
                        </div>
                        <div className="voucher-detail__credentials">
                            <span className="voucher-skel voucher-detail__skel-credential" />
                            <span className="voucher-skel voucher-detail__skel-credential" />
                        </div>
                        <span className="voucher-skel voucher-detail__skel-action" />
                        <span className="voucher-skel voucher-detail__skel-notice" />
                    </div>
                ) : selected ? (
                    <div className="voucher-detail">
                        <div className="voucher-detail__hero">
                            <div
                                className={`voucher-detail__hero-logo${
                                    getVoucherLogo(selected)
                                        ? ' voucher-detail__hero-logo--image'
                                        : ''
                                }`}
                            >
                                {getVoucherLogo(selected) ? (
                                    <img src={getVoucherLogo(selected)} alt="" />
                                ) : (
                                    <span>{getVoucherName(selected).charAt(0)}</span>
                                )}
                            </div>
                            <div className="voucher-detail__hero-copy">
                                <strong>{getVoucherName(selected)}</strong>
                                {getVoucherRegion(selected) ? (
                                    <span>{getVoucherRegion(selected)}</span>
                                ) : null}
                                <em>{getVoucherAmount(selected)}</em>
                            </div>
                        </div>

                        {getVoucherRegion(selected) ? (
                            <div className="voucher-detail__row">
                                <span>{t('labels.country')}</span>
                                <span>{getVoucherRegion(selected)}</span>
                            </div>
                        ) : null}
                        {getVoucherDate(selected) ? (
                            <div className="voucher-detail__row">
                                <span>{t('labels.purchased')}</span>
                                <span>{getVoucherDate(selected)}</span>
                            </div>
                        ) : null}

                        <div className="voucher-detail__credentials">
                            <div className="voucher-detail__credential">
                                <span>{t('labels.code')}</span>
                                <code>
                                    {revealed
                                        ? codeIsHttpUrl
                                            ? (
                                                <a
                                                    href={selectedCode}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {selectedCode}
                                                </a>
                                            )
                                            : selectedCode || t('fallback.notAvailable')
                                        : '•••• •••• •••• ••••'}
                                </code>
                                {revealed && !codeIsHttpUrl ? (
                                    <Button
                                        type="text"
                                        icon={<CopyOutlined />}
                                        aria-label={t('a11y.copyCode')}
                                        disabled={!selectedCode}
                                        onClick={() =>
                                            void copyVoucherValue(selectedCode, 'code')
                                        }
                                    />
                                ) : null}
                            </div>
                            {selected.pin && (
                            <div className="voucher-detail__credential">
                                <span>{t('labels.pin')}</span>
                                <code>
                                    {revealed ? selected.pin || t('fallback.notAvailable') : '•••••••••'}
                                </code>
                                {revealed ? (
                                    <Button
                                        type="text"
                                        icon={<CopyOutlined />}
                                        aria-label={t('a11y.copyPin')}
                                        disabled={!selected.pin}
                                        onClick={() =>
                                            void copyVoucherValue(selected.pin, 'pin')
                                        }
                                    />
                                ) : null}
                            </div>
                            )}
                        </div>
                        {revealed && selected.redemptionUrl ? (
                            <div className="voucher-detail__qr">
                                <QRCodeSVG
                                    value={selected.redemptionUrl}
                                    size={180}
                                    marginSize={2}
                                    bgColor="#eaf5fd"
                                    fgColor="#081321"
                                    level="M"
                                    title={t('a11y.redemptionQr')}
                                />
                                <span>{t('detail.scanToRedeem')}</span>
                            </div>
                        ) : null}
                        {!revealed ? (
                            <Button
                                type="primary"
                                className="voucher-detail__action"
                                icon={<EyeOutlined />}
                                onClick={() => setRevealed(true)}
                                loading={detailLoading}
                                disabled={detailLoading}
                            >
                                {t('actions.reveal')}
                            </Button>
                        ) : null}
                        <div className="vouchers-page__notice voucher-detail__notice">
                            {t('detail.privateNotice')}
                        </div>
                    </div>
                ) : null}
            </Modal>
        </div>
    );
}

export default VouchersPage;
